import { useEffect, useState } from "react";
import { ArrowRight, Camera, ClipboardCopy, Loader2, RefreshCw, Sparkles, X } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import PhotoUpload from "@/components/PhotoUpload";
import AuthImage from "@/components/AuthImage";
import { api } from "@/lib/api";

function ActionPlan({ plan, onCopy, onRefresh, refreshing }) {
  const list = (title, items) =>
    items?.length > 0 && (
      <div>
        <div className="text-[11px] font-bold uppercase tracking-wider text-stone-500">{title}</div>
        <ul className="mt-1 space-y-1">
          {items.map((it, i) => (
            <li key={i} className="flex gap-2 text-xs text-stone-800">
              <span className="text-emerald-700 font-bold">{i + 1}.</span> {it}
            </li>
          ))}
        </ul>
      </div>
    );
  return (
    <div className="rounded-lg border border-emerald-200 bg-emerald-50/60 p-3 space-y-3" data-testid="occurrence-action-plan">
      {plan.root_cause && (
        <div className="text-xs text-stone-800">
          <b className="text-emerald-900">Causa provável:</b> {plan.root_cause}
        </div>
      )}
      {list("Ações imediatas", plan.immediate_actions)}
      {list("Ações preventivas", plan.preventive_actions)}
      <div className="grid sm:grid-cols-3 gap-2 text-[11px] text-stone-700">
        {plan.responsible_role && <div><b>Responsável:</b> {plan.responsible_role}</div>}
        {plan.deadline_hint && <div><b>Prazo:</b> {plan.deadline_hint}</div>}
        {plan.verification && <div><b>Comprovação:</b> {plan.verification}</div>}
      </div>
      <div className="flex flex-wrap gap-2 pt-1">
        <Button size="sm" className="bg-emerald-800 hover:bg-emerald-900 text-white" onClick={onCopy} data-testid="occurrence-plan-copy">
          <ClipboardCopy className="h-3.5 w-3.5 mr-1.5" /> Copiar para notas
        </Button>
        <Button size="sm" variant="ghost" onClick={onRefresh} disabled={refreshing} className="text-stone-600" data-testid="occurrence-plan-refresh">
          {refreshing ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5 mr-1.5" />} Gerar novamente
        </Button>
      </div>
    </div>
  );
}

export default function OccurrenceTreatment({ occurrence, onAdvance, onSaved }) {
  const [notes, setNotes] = useState(occurrence.resolution_notes || "");
  const [plan, setPlan] = useState(occurrence.ai_action_plan || null);
  const [afterPhotos, setAfterPhotos] = useState(occurrence.resolution_photos || []);
  const [pending, setPending] = useState("");
  const [loadingPlan, setLoadingPlan] = useState(false);

  useEffect(() => {
    setNotes(occurrence.resolution_notes || "");
    setPlan(occurrence.ai_action_plan || null);
    setAfterPhotos(occurrence.resolution_photos || []);
    setPending("");
  }, [occurrence.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const save = async (patch) => {
    try {
      const { data } = await api.patch(`/occurrences/${occurrence.id}`, patch);
      onSaved?.(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Falha ao salvar");
    }
  };

  const suggest = async (refresh = false) => {
    setLoadingPlan(true);
    try {
      const { data } = await api.post(`/occurrences/${occurrence.id}/suggest-action${refresh ? "?refresh=true" : ""}`);
      setPlan(data);
      toast.success(data.with_photo ? "Plano sugerido com base na foto e no registro" : "Plano de ação sugerido");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "IA indisponível no momento");
    } finally {
      setLoadingPlan(false);
    }
  };

  const copyToNotes = () => {
    if (!plan?.notes_text) return;
    const next = notes.trim() ? `${notes.trim()}\n\n${plan.notes_text}` : plan.notes_text;
    setNotes(next);
    save({ resolution_notes: next });
    toast.success("Plano copiado para as notas");
  };

  const addAfterPhoto = (url) => {
    if (!url) {
      setPending("");
      return;
    }
    const next = [...afterPhotos, url];
    setAfterPhotos(next);
    setPending("");
    save({ resolution_photos: next });
  };

  const removeAfterPhoto = (url) => {
    const next = afterPhotos.filter((u) => u !== url);
    setAfterPhotos(next);
    save({ resolution_photos: next });
  };

  const canAttachAfter = ["in_progress", "resolved", "validated"].includes(occurrence.status);

  return (
    <div className="space-y-4">
      <div>
        <div className="flex items-center justify-between gap-2">
          <Label className="text-sm font-body flex items-center gap-1.5">
            <Sparkles className="h-4 w-4 text-emerald-700" /> Ação corretiva sugerida (IA)
          </Label>
          {!plan && (
            <Button size="sm" variant="outline" onClick={() => suggest(false)} disabled={loadingPlan} className="bg-white border-emerald-200 text-emerald-800 hover:bg-emerald-50" data-testid="occurrence-suggest-action">
              {loadingPlan ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5 mr-1.5" />}
              {loadingPlan ? "Analisando…" : "Sugerir plano de ação"}
            </Button>
          )}
        </div>
        {plan && <div className="mt-2"><ActionPlan plan={plan} onCopy={copyToNotes} onRefresh={() => suggest(true)} refreshing={loadingPlan} /></div>}
      </div>

      <div>
        <Label className="text-sm font-body">Notas de tratamento / resolução</Label>
        <Textarea
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          onBlur={() => notes !== (occurrence.resolution_notes || "") && save({ resolution_notes: notes })}
          rows={4}
          className="mt-1 bg-white"
          data-testid="occurrence-resolution"
        />
      </div>

      {canAttachAfter && (
        <div data-testid="occurrence-after-photos">
          <Label className="text-sm font-body flex items-center gap-1.5">
            <Camera className="h-4 w-4 text-emerald-700" /> Foto do "depois" (comprovação)
          </Label>
          <div className="mt-2 flex flex-wrap gap-3 items-start">
            {afterPhotos.map((u, i) => (
              <div key={u} className="relative" data-testid={`occurrence-after-photo-${i}`}>
                <AuthImage src={u} alt={`Depois ${i + 1}`} className="h-24 w-32 object-cover rounded-md border border-emerald-200" />
                {occurrence.status !== "validated" && (
                  <button type="button" onClick={() => removeAfterPhoto(u)} className="absolute -top-2 -right-2 h-6 w-6 rounded-full bg-rose-600 text-white shadow flex items-center justify-center hover:bg-rose-700" aria-label="Remover foto" data-testid={`occurrence-after-photo-remove-${i}`}>
                    <X className="h-3 w-3" />
                  </button>
                )}
              </div>
            ))}
            {occurrence.status !== "validated" && (
              <PhotoUpload value={pending} onChange={addAfterPhoto} allowGallery showAiValidate={false} testId="occurrence-after-upload" />
            )}
          </div>
        </div>
      )}

      <div className="flex flex-wrap gap-2 pt-2 border-t border-stone-200">
        {occurrence.status === "open" && (
          <Button size="sm" onClick={() => onAdvance("in_progress", { resolution_notes: notes })} data-testid="occurrence-start">
            Iniciar tratamento <ArrowRight className="h-3 w-3 ml-1" />
          </Button>
        )}
        {occurrence.status === "in_progress" && (
          <Button size="sm" className="bg-emerald-700 hover:bg-emerald-800 text-white" onClick={() => onAdvance("resolved", { resolution_notes: notes, resolution_photos: afterPhotos })} data-testid="occurrence-resolve">
            Marcar resolvida {afterPhotos.length > 0 && `· ${afterPhotos.length} foto${afterPhotos.length > 1 ? "s" : ""}`}
          </Button>
        )}
        {occurrence.status === "resolved" && (
          <Button size="sm" className="bg-stone-800 hover:bg-stone-700 text-white" onClick={() => onAdvance("validated", { resolution_notes: notes })} data-testid="occurrence-validate">
            Validar
          </Button>
        )}
      </div>
    </div>
  );
}
